import { useState, useEffect } from "react";
import { supabase } from "./supabase";

export default function PlayerList({ onEnroll, academy }) {
  const [userId, setUserId] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeBatch, setActiveBatch] = useState("All");
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const batches = ["All", "Morning", "Evening", "Weekend", "U-14", "U-19"];

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUserId(user?.id));
  }, []);

  useEffect(() => { if (userId) fetchPlayers(); }, [userId]);

  const fetchPlayers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("players")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error) setPlayers(data || []);
    setLoading(false);
  };

  const filtered = players.filter((p) => {
    const matchSearch =
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.student_id?.toLowerCase().includes(search.toLowerCase()) ||
      p.parent_name?.toLowerCase().includes(search.toLowerCase());
    const matchBatch = activeBatch === "All" || p.batch === activeBatch;
    return matchSearch && matchBatch;
  });

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const avatarColors = [
    "#1a3a1b", "#2d6a4f", "#1a472a", "#0d3321",
    "#2c5f2e", "#386641", "#1b4332", "#40916c",
  ];

  const getColor = (name) => {
    if (!name) return avatarColors[0];
    const i = name.charCodeAt(0) % avatarColors.length;
    return avatarColors[i];
  };

  if (selectedPlayer) {
    return <PlayerProfile player={selectedPlayer} onBack={() => setSelectedPlayer(null)} onRefresh={fetchPlayers} />;
  }

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div style={styles.logoRow}>
          <div style={styles.logoIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 17l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 12l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={styles.logoText}>{academy?.name?.toUpperCase() || "THE COACH ROOM"}</span>
        </div>
        <div style={styles.headerBottom}>
          <div>
            <h2 style={styles.title}>Players</h2>
            <p style={styles.sub}>{players.length} enrolled · {filtered.length} shown</p>
          </div>
          <button style={styles.enrollBtn} onClick={onEnroll}>+ Enroll</button>
        </div>
      </div>

      {/* SEARCH */}
      <div style={styles.searchWrap}>
        <div style={styles.searchBox}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8" stroke="#9ca3af" strokeWidth="2"/>
            <path d="M21 21l-4.35-4.35" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            style={styles.searchInput}
            placeholder="Search by name, ID or parent..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button style={styles.clearBtn} onClick={() => setSearch("")}>✕</button>
          )}
        </div>
      </div>

      {/* BATCH FILTER */}
      <div style={styles.batchRow}>
        {batches.map((b) => (
          <button
            key={b}
            style={{
              ...styles.batchChip,
              background: activeBatch === b ? "#1a3a1b" : "white",
              color: activeBatch === b ? "#d4a017" : "#6b7280",
              border: activeBatch === b ? "1.5px solid #1a3a1b" : "1.5px solid #e8e0d0",
              fontWeight: activeBatch === b ? "700" : "400",
            }}
            onClick={() => setActiveBatch(b)}
          >
            {b}
          </button>
        ))}
      </div>

      {/* PLAYER LIST */}
      <div style={styles.list}>
        {loading ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>⏳</div>
            <div style={styles.emptyText}>Loading players...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>🏏</div>
            <div style={styles.emptyText}>
              {search ? "No players match your search" : "No players enrolled yet"}
            </div>
            <div style={styles.emptySub}>
              {search ? "Try a different name or ID" : "Tap + Enroll to add your first player"}
            </div>
            {!search && (
              <button style={styles.emptyBtn} onClick={onEnroll}>+ Enroll First Player</button>
            )}
          </div>
        ) : (
          <>
            {filtered.map((player, i) => (
              <button
                key={player.id}
                style={{
                  ...styles.playerCard,
                  animationDelay: `${i * 40}ms`,
                }}
                onClick={() => setSelectedPlayer(player)}
              >
                {/* AVATAR */}
                <div style={{
                  ...styles.avatar,
                  background: getColor(player.name),
                }}>
                  {getInitials(player.name)}
                </div>

                {/* INFO */}
                <div style={styles.playerInfo}>
                  <div style={styles.playerName}>{player.name}</div>
                  <div style={styles.playerMeta}>
                    <span style={styles.metaChip}>{player.student_id}</span>
                    {player.batch && (
                      <span style={styles.metaChip}>{player.batch}</span>
                    )}
                  </div>
                  {player.parent_name && (
                    <div style={styles.parentName}>Parent: {player.parent_name}</div>
                  )}
                </div>

                {/* RIGHT */}
                <div style={styles.playerRight}>
                  <div style={styles.feeAmount}>
                    {player.fee_amount ? `₹${Number(player.fee_amount).toLocaleString("en-IN")}` : "—"}
                  </div>
                  <div style={styles.feeLabel}>/ month</div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginTop: "0.5rem" }}>
                    <path d="M9 18l6-6-6-6" stroke="#d4a017" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </button>
            ))}
            <div style={styles.listFooter}>
              {filtered.length} player{filtered.length !== 1 ? "s" : ""} shown
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── PLAYER PROFILE ───────────────────────────────────────────
function PlayerProfile({ player, onBack, onRefresh }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Remove ${player.name} from the academy?`)) return;
    setDeleting(true);
    await supabase.from("players").delete().eq("id", player.id);
    onRefresh();
    onBack();
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const avatarColors = ["#1a3a1b", "#2d6a4f", "#1a472a", "#0d3321", "#2c5f2e"];
  const getColor = (name) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];
  const getInitials = (name) => name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div style={styles.logoRow}>
          <button onClick={onBack} style={styles.backBtn}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M12 19l-7-7 7-7" stroke="#d4a017" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <span style={styles.logoText}>{academy?.name?.toUpperCase() || "THE COACH ROOM"}</span>
        </div>
        <div style={styles.profileHero}>
          <div style={{ ...styles.profileAvatar, background: getColor(player.name) }}>
            {getInitials(player.name)}
          </div>
          <div>
            <h2 style={styles.profileName}>{player.name}</h2>
            <div style={styles.profileId}>{player.student_id}</div>
          </div>
        </div>
      </div>

      {/* DETAILS */}
      <div style={styles.profileBody}>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Player Details</div>
          <div style={styles.detailCard}>
            <DetailRow label="Student ID" value={player.student_id} />
            <DetailRow label="Batch" value={player.batch || "—"} />
            <DetailRow label="Date Joined" value={formatDate(player.joined_date)} />
            <DetailRow label="Monthly Fee" value={player.fee_amount ? `₹${Number(player.fee_amount).toLocaleString("en-IN")}` : "—"} last />
          </div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Parent / Guardian</div>
          <div style={styles.detailCard}>
            <DetailRow label="Name" value={player.parent_name || "—"} />
            <DetailRow label="WhatsApp" value={player.whatsapp ? `+91 ${player.whatsapp}` : "—"} last />
          </div>
        </div>

        {player.whatsapp && (
          <a
            href={`https://wa.me/91${player.whatsapp.replace(/\s/g, "")}`}
            target="_blank"
            rel="noreferrer"
            style={styles.waBtn}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="#25D366"/>
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.558 4.122 1.528 5.855L.057 23.882l6.174-1.616A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.273-1.532l-.378-.224-3.918 1.026 1.044-3.808-.246-.392A9.818 9.818 0 1112 21.818z" fill="#25D366"/>
            </svg>
            Message Parent on WhatsApp
          </a>
        )}

        <button
          style={styles.deleteBtn}
          onClick={handleDelete}
          disabled={deleting}
        >
          {deleting ? "Removing..." : "Remove Player from Academy"}
        </button>

      </div>
    </div>
  );
}

function DetailRow({ label, value, last }) {
  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "0.75rem 1rem",
      borderBottom: last ? "none" : "1px solid #f0ebe0",
    }}>
      <span style={{ fontSize: "0.78rem", color: "#6b7280", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</span>
      <span style={{ fontSize: "0.88rem", color: "#1a1a1a", fontWeight: "600" }}>{value}</span>
    </div>
  );
}

const styles = {
  container: {
    flex: 1,
    overflowY: "auto",
    background: "#f0ebe0",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    minHeight: "100%",
  },
  header: {
    background: "#111a12",
    padding: "1.4rem 1.5rem 1.8rem",
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    marginBottom: "1.2rem",
  },
  logoIcon: {
    width: "32px", height: "32px",
    background: "rgba(212,160,23,0.15)",
    border: "1px solid rgba(212,160,23,0.4)",
    borderRadius: "8px",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  logoText: {
    color: "white",
    fontSize: "0.72rem",
    fontWeight: "800",
    letterSpacing: "3px",
  },
  backBtn: {
    background: "rgba(212,160,23,0.1)",
    border: "1px solid rgba(212,160,23,0.3)",
    borderRadius: "8px",
    width: "32px", height: "32px",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", padding: 0, flexShrink: 0,
  },
  headerBottom: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  title: {
    color: "white",
    fontSize: "1.5rem",
    fontWeight: "700",
    margin: 0,
    letterSpacing: "-0.3px",
  },
  sub: {
    color: "rgba(255,255,255,0.4)",
    fontSize: "0.78rem",
    margin: "0.2rem 0 0",
    fontWeight: "300",
  },
  enrollBtn: {
    background: "#d4a017",
    color: "#1a3a1b",
    border: "none",
    padding: "0.55rem 1.1rem",
    fontSize: "0.82rem",
    fontWeight: "800",
    cursor: "pointer",
    letterSpacing: "0.5px",
  },
  searchWrap: {
    padding: "1rem 1.2rem 0.5rem",
    background: "#f0ebe0",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    background: "white",
    border: "1.5px solid #e8e0d0",
    padding: "0.65rem 0.9rem",
  },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "0.88rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#1a1a1a",
    background: "transparent",
  },
  clearBtn: {
    background: "none",
    border: "none",
    color: "#9ca3af",
    cursor: "pointer",
    fontSize: "0.8rem",
    padding: "0 0.2rem",
  },
  batchRow: {
    display: "flex",
    gap: "0.5rem",
    padding: "0.6rem 1.2rem",
    overflowX: "auto",
    scrollbarWidth: "none",
  },
  batchChip: {
    padding: "0.35rem 0.8rem",
    fontSize: "0.75rem",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    letterSpacing: "0.3px",
    transition: "all 0.15s",
    flexShrink: 0,
  },
  list: {
    padding: "0.5rem 1.2rem 1.5rem",
  },
  playerCard: {
    width: "100%",
    background: "white",
    border: "1px solid #e8e0d0",
    borderLeft: "3px solid #1a3a1b",
    padding: "0.9rem 1rem",
    marginBottom: "0.6rem",
    display: "flex",
    alignItems: "center",
    gap: "0.9rem",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    transition: "box-shadow 0.15s",
    boxSizing: "border-box",
  },
  avatar: {
    width: "42px", height: "42px",
    borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#d4a017",
    fontSize: "0.85rem",
    fontWeight: "800",
    flexShrink: 0,
    letterSpacing: "0.5px",
  },
  playerInfo: { flex: 1, minWidth: 0 },
  playerName: {
    fontSize: "0.95rem",
    fontWeight: "700",
    color: "#1a1a1a",
    marginBottom: "0.3rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  playerMeta: {
    display: "flex",
    gap: "0.4rem",
    marginBottom: "0.2rem",
    flexWrap: "wrap",
  },
  metaChip: {
    background: "#f0ebe0",
    color: "#4a5568",
    fontSize: "0.65rem",
    fontWeight: "700",
    padding: "0.15rem 0.5rem",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  parentName: {
    fontSize: "0.72rem",
    color: "#9ca3af",
    fontWeight: "300",
  },
  playerRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    flexShrink: 0,
  },
  feeAmount: {
    fontSize: "0.95rem",
    fontWeight: "800",
    color: "#1a3a1b",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  feeLabel: {
    fontSize: "0.62rem",
    color: "#9ca3af",
    fontWeight: "300",
  },
  empty: {
    textAlign: "center",
    padding: "4rem 2rem",
  },
  emptyIcon: { fontSize: "2.5rem", marginBottom: "1rem" },
  emptyText: {
    fontWeight: "700",
    fontSize: "1rem",
    color: "#1a3a1b",
    marginBottom: "0.4rem",
  },
  emptySub: {
    fontSize: "0.83rem",
    color: "#6b7280",
    marginBottom: "1.5rem",
    fontWeight: "300",
  },
  emptyBtn: {
    background: "#1a3a1b",
    color: "#d4a017",
    border: "none",
    padding: "0.8rem 1.5rem",
    fontSize: "0.85rem",
    fontWeight: "700",
    cursor: "pointer",
    letterSpacing: "0.5px",
  },
  listFooter: {
    textAlign: "center",
    fontSize: "0.72rem",
    color: "#9ca3af",
    padding: "1rem 0 0.5rem",
    fontWeight: "300",
  },
  // Profile styles
  profileHero: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  },
  profileAvatar: {
    width: "56px", height: "56px",
    borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#d4a017",
    fontSize: "1.1rem",
    fontWeight: "800",
    flexShrink: 0,
    border: "2px solid rgba(212,160,23,0.3)",
  },
  profileName: {
    color: "white",
    fontSize: "1.3rem",
    fontWeight: "700",
    margin: 0,
  },
  profileId: {
    color: "#d4a017",
    fontSize: "0.75rem",
    fontWeight: "700",
    letterSpacing: "1px",
    marginTop: "0.2rem",
  },
  profileBody: {
    padding: "1.2rem 1.2rem 2rem",
  },
  section: {
    marginBottom: "1.2rem",
  },
  sectionTitle: {
    fontSize: "0.68rem",
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: "1.5px",
    color: "#6b7280",
    marginBottom: "0.5rem",
    paddingLeft: "0.2rem",
  },
  detailCard: {
    background: "white",
    border: "1px solid #e8e0d0",
    overflow: "hidden",
  },
  waBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.6rem",
    width: "100%",
    padding: "0.9rem",
    background: "#25D366",
    color: "white",
    border: "none",
    fontSize: "0.88rem",
    fontWeight: "700",
    cursor: "pointer",
    letterSpacing: "0.3px",
    marginBottom: "0.8rem",
    textDecoration: "none",
    boxSizing: "border-box",
  },
  deleteBtn: {
    width: "100%",
    padding: "0.9rem",
    background: "white",
    color: "#e53e3e",
    border: "1.5px solid #feb2b2",
    fontSize: "0.85rem",
    fontWeight: "700",
    cursor: "pointer",
    letterSpacing: "0.3px",
    boxSizing: "border-box",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};